/* eslint-disable @typescript-eslint/no-shadow */
import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { WebSocketService } from 'src/app/core/services/web-socket.service';
import { Campaign } from 'src/app/models/campaign';
import { ACTIONS, STATUSES } from 'src/app/shared/constants/general-constants';
import { SharedModule } from '../../../shared/shared.module';
import { NgxGenericModalComponent } from '../../../shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { LoadContent } from '../../content/redux/content.actions';
import { ContentState } from '../../content/redux/content.state';
import { InsightsState, LoadInsights, LoadInsightsInactive } from '../../insights/redux';
import { LoadSurveys, SurveysState } from '../../surveys/redux';
import { CampaignState, LoadCampaigns, ReloadCampaign } from '../redux';
import { CampaignService } from '../services/campaign.service';
import { CampaignTerminateModalComponent } from './campaign-terminate-modal/campaign-terminate-modal.component';
import { DeleteCampaignComponent } from './delete-campaign/delete-campaign.component';
import { EditCampaignComponent } from './edit-campaign/edit-campaign.component';
import { StatsCampaignComponent } from './stats-campaign/stats-campaign.component';

@Component({
  selector: 'app-campaigns',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './campaigns.component.html',
  styleUrls: ['./campaigns.component.css'],
  providers: [DecimalPipe],
  imports: [SharedModule, RouterOutlet]
})
export class CampaignsComponent implements OnInit, OnDestroy {
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);
  private content$: Observable<any[]> = inject(Store).select(ContentState.content);
  private unsubscribe: Subject<void> = new Subject();

  public tableData;
  public total$: Observable<number>;
  public showActions = [
    ACTIONS.CREATE,
    ACTIONS.EDIT,
    ACTIONS.STATS,
    ACTIONS.START,
    ACTIONS.STOP,
    ACTIONS.CLONE,
    ACTIONS.DELETE,
    ACTIONS.FAVORITE
  ];
  private contents;

  constructor(
    private service: CampaignService,
    private store: Store,
    private dialogService: NbDialogService,
    private router: Router,
    private socketService: WebSocketService,
    private toastrService: NbToastrService
  ) {
    if (!this.store.selectSnapshot(InsightsState.insightsActive)) {
      this.store.dispatch(LoadInsights);
    }
    if (!this.store.selectSnapshot(InsightsState.insightsInactive)) {
      this.store.dispatch(LoadInsightsInactive);
    }
    if (!this.store.selectSnapshot(ContentState.content)) {
      this.store.dispatch(LoadContent);
    }
    if (!this.store.selectSnapshot(SurveysState.surveys)) {
      this.store.dispatch(LoadSurveys);
    }
    if (!this.store.selectSnapshot(CampaignState.campaign)) {
      this.store.dispatch(LoadCampaigns);
    }
  }

  public ngOnInit() {
    this.campaign$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.tableData = data));
    this.content$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.contents = data));
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public clickedButton($event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.tableData = undefined;
      this.store.dispatch(ReloadCampaign);
    }
    if ($event.action === ACTIONS.CREATE) {
      this.dialogService.open(EditCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          campaign: new Campaign()
        }
      });
    }
    if ($event.action === ACTIONS.CLONE) {
      const clonedCampaign = Campaign.clone($event.data);
      clonedCampaign.id = null;
      this.dialogService.open(EditCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          action: $event.action,
          campaign: clonedCampaign
        }
      });
    }
    if ($event.action === ACTIONS.VIEW) {
      const campaign: Campaign = $event.data;
      this.router.navigate([`/campaigns/table/` + campaign.id]);
    }
    if ($event.action === ACTIONS.STATS) {
      const campaign: Campaign = $event.data;
      this.dialogService.open(StatsCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          campaign
        }
      });
    }
    if ($event.action === ACTIONS.START) {
      const campaign: Campaign = $event.data;
      const content = this.contents.find((element) => element.content_id === campaign.content_id);
      let valid_campaign = this.service.isValidCampaignType(campaign, content);

      if ($event.data.status === STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Cannot start a running campaign`, { status: 'danger' });
      } else if (valid_campaign) {
        this.dialogService
          .open(NgxGenericModalComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              title: $localize`Are you sure you want to "Run": `,
              message: campaign.id
            }
          })
          .onClose.subscribe((data) => {
            if (data) {
              this.socketService.setUp();
              this.service
                .runCampaignJob(campaign)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((data) => {
                  setTimeout(() => {
                    this.socketService.sendMessage(data.job_id, campaign.id);
                  }, 2000);
                });
            }
          });
      } else {
        this.dialogService.open(NgxGenericModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            title: $localize`Run Denied: Invalid Campaign Configuration`,
            message: $localize`Invalid Configuration: The content attached to this campaign is set up for the Prime App and is not compatible with ${campaign.campaign_type} campaign type. 
            Content for Prime is only compatible with Contextual Message, eCommerce, and Map Dirty campaign types.`,
            displayOkButton: true
          }
        });
      }
    }
    if ($event.action === ACTIONS.STOP) {
      if ($event.data.status !== STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Campaign is not running`, { status: 'danger' });
      } else {
        const campaign: Campaign = $event.data;
        this.dialogService.open(CampaignTerminateModalComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: { campaign }
        });
      }
    }
    if ($event.action === ACTIONS.EDIT) {
      if ($event.data.status === STATUSES.RUNNING) {
        this.toastrService.show(status, $localize`Cannot edit a running campaign`, { status: 'danger' });
      } else {
        const campaign: Campaign = $event.data;
        this.dialogService.open(EditCampaignComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            action: $event.action,
            campaign: Campaign.copy(campaign) as Campaign
          }
        });
      }
    }
    if ($event.action === ACTIONS.DELETE) {
      const campaign: Campaign = $event.data;
      this.dialogService.open(DeleteCampaignComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          campaign: Campaign.copy(campaign) as Campaign
        }
      });
    }
  }
}
